import React from 'react'
import { Slider } from 'antd'
import { Trans } from '@lingui/react/macro'
import { MUSIC_DEFAULT_CREDITS } from '@loc/server/constant/common'
import { orderBy } from 'lodash-es'

import { PlansProvider, usePlansContext } from '../context'
import { btnCls } from '../PlanCard/classes'
import {
  experimentPlanConfigs,
  getExperimentColumnKey,
  getExperimentListItems,
  getProductPriceSuffix,
  getProductPriceText,
} from '../planDisplayContent'
import SignUpBox from '../../Modals/Tips/SignUpModal/SignUpBox'
import PlanButton from '../PlanButton'

import type {
  ICommonPlansBlockProps,
  TProduct,
} from '@/contexts/PlanContext/type'
import type { TUseSubLog } from '@/contexts/SiteSpecificContext/hooks/subLog'

import { EPaidProductType } from '@/contexts/PlanContext/type'
import { getProductType } from '@/contexts/PlanContext/helpers/getProductType'
import { EPricingStep } from '@/contexts/PlanContext/hooks/payStep'
import InnerContent from '@/_blocks/Plan/InnerContent'
import ListItem from '@/_blocks/Plan/ListItem'
import StyledButton from '@/pages/tad.ai/_components/StyledButton'
import { PayCard } from '@/_blocks/Plan/PayCard'
import LazyImage from '@/components/LazyImage'


import { cls, tw } from 'tagged-classnames-free';
import { useState, useMemo, useEffect } from 'react';
type TCurrentCreditsProduct = NonNullable<
  TUseSubLog['creditsPlan']
>['productsSub']

function PreviewButton({ text }: { text: React.ReactNode }) {
  return (
    <StyledButton
      disabled
      className={`${btnCls} !h-8 !rounded-[8px] !border-0 !bg-[#323247] !text-[#707080]`}
    >
      {text}
    </StyledButton>
  )
}

function getExperimentColumnProducts({
  columnKey,
  creditsProducts,
  currentProduct,
}: {
  columnKey: (typeof experimentPlanConfigs)[number]['key']
  creditsProducts: TProduct[]
  currentProduct?: TCurrentCreditsProduct
}) {
  const currentProductColumnKey = getExperimentColumnKey(currentProduct)

  const filteredProducts = creditsProducts.filter((product) => {
    const isCurrentProduct = currentProduct?.priceId === product.priceId
    const experimentColumnKey = getExperimentColumnKey(product)

    if (columnKey === 'basic') {
      return experimentColumnKey === 'basic'
    }

    if (columnKey === 'standard') {
      return experimentColumnKey === 'standard'
    }

    // 某些高级套餐不一定能被 getExperimentColumnKey 直接识别，
    // 但如果它正好是用户当前套餐，仍要兜底展示在 Pro 列里，避免“当前套餐消失”。
    return (
      experimentColumnKey === 'pro' ||
      (isCurrentProduct &&
        currentProductColumnKey !== 'basic' &&
        currentProductColumnKey !== 'standard' &&
        [
          EPaidProductType.PRO,
          EPaidProductType.UNLIMITED,
          EPaidProductType.ULTIMATE,
        ].includes(getProductType({ product }) as EPaidProductType))
    )
  })

  return orderBy(filteredProducts, [(product) => product.usageCount], ['asc'])
}

function getTierLabelClassName(
  index: number,
  selectedIndex: number,
  total: number,
) {
  const alignmentClassName =
    index === 0
      ? 'text-left'
      : index === total - 1
        ? 'text-right'
        : 'text-center'

  const colorClassName =
    index === selectedIndex
      ? 'text-f-text'
      : index < selectedIndex
        ? 'text-white/65'
        : 'text-white/45'

  return cls`
    flex-[1_0_0] text-xs leading-4 ${alignmentClassName} ${colorClassName}
  `
}

function ExperimentPlanCard({
  config,
}: {
  config: (typeof experimentPlanConfigs)[number]
}) {
  const { appName, payStepInfo, plan, planProductListInfo, isModal } =
    usePlansContext()
  const [selectedIndex, setSelectedIndex] = useState(0)

  const actualProducts = useMemo(() => {
    return getExperimentColumnProducts({
      columnKey: config.key,
      creditsProducts: planProductListInfo.creditsProducts,
      currentProduct: plan.creditsPlan?.productsSub,
    })
  }, [
    config.key,
    plan.creditsPlan?.productsSub,
    planProductListInfo.creditsProducts,
  ])

  const defaultSelectedIndex = useMemo(() => {
    const currentPriceId = plan.creditsPlan?.productsSub.priceId

    if (!currentPriceId) {
      return 0
    }

    const matchedIndex = actualProducts.findIndex((product) => {
      return product.priceId === currentPriceId
    })

    return matchedIndex >= 0 ? matchedIndex : 0
  }, [actualProducts, plan.creditsPlan?.productsSub.priceId])

  useEffect(() => {
    setSelectedIndex(defaultSelectedIndex)
  }, [defaultSelectedIndex])

  const selectedProduct = actualProducts[selectedIndex]
  const featureItems = useMemo(() => {
    if (!selectedProduct) {
      return []
    }

    return getExperimentListItems(selectedProduct, { isModal })
  }, [isModal, selectedProduct])

  const buyButton = useMemo(() => {
    if (!selectedProduct) {
      return <PreviewButton text={<Trans>Preview Only</Trans>} />
    }

    const buttonType =
      config.key === 'pro' ? EPaidProductType.PRO : EPaidProductType.BASIC

    return (
      <PlanButton
        appName={appName}
        type={buttonType}
        payStepInfo={payStepInfo}
        productPriceCredits={selectedProduct}
      />
    )
  }, [appName, config.key, payStepInfo, selectedProduct])

  const isPrimary = config.key === 'pro'

  return (
    <li
      className={cls`
        relative min-h-[448px] w-[240px] rounded-[16px] px-5 pb-6 pt-4
        ${isPrimary ? 'bg-[linear-gradient(159.53deg,#040404_4.52%,#1A1A24_50.57%,#040404_96.61%)]' : 'bg-[#252533]'}
      `}
    >
      <div className='flex min-h-6 items-center gap-1'>
        {config.key === 'pro' ? (
          <LazyImage
            src='/images/cus-tad-member-second-color.png'
            alt='cus-tad-member-second-color'
            width={20}
            height={20}
          />
        ) : null}
        {config.key === 'standard' ? (
          <span className='i-cus--tad-member-first-color size-5 text-[#ff9e45]' />
        ) : null}
        <span className='text-f-text text-base leading-6'>{config.title}</span>
        {config.badge ? (
          <span className='inline-flex h-[18px] items-center justify-center rounded-[4px] border border-[#663c00] bg-[#2a1c06] px-[5px] text-xs leading-4 text-[#ffaa33]'>
            {config.badge}
          </span>
        ) : null}
      </div>

      <div className='mt-4 flex flex-col gap-2'>
        <div className='flex items-end gap-1'>
          <div className='inline-flex items-end gap-[2px]'>
            <span
              className={cls`
                pb-[5px] text-sm leading-5
                ${isPrimary ? 'text-f-primary' : 'text-f-text'}
              `}
            >
              $
            </span>
            <span
              className={cls`
                text-4xl font-semibold
                ${isPrimary ? 'text-f-primary' : 'text-f-text'}
              `}
            >
              {selectedProduct ? getProductPriceText(selectedProduct) : '--'}
            </span>
          </div>
          <span className='text-f-text-tertiary pb-[5px] text-sm leading-5'>
            {selectedProduct ? getProductPriceSuffix(selectedProduct) : null}
          </span>
        </div>

        <div className='mb-4'>
          <Slider
            min={0}
            max={Math.max(actualProducts.length - 1, 0)}
            step={1}
            dots
            value={selectedIndex}
            tooltip={{ open: false }}
            onChange={(value) => setSelectedIndex(Number(value))}
            disabled={actualProducts.length <= 1}
            className={cls`
              [&_.coco-slider-handle]:bg-f-primary [&_.coco-slider-dot-active]:!bg-f-primary 
              [&_.coco-slider-dot-active]:!border-f-primary my-0 
              [&_.coco-slider-dot:not(.coco-slider-dot-active)]:!border-[#323247]
              [&_.coco-slider-dot:not(.coco-slider-dot-active)]:!bg-[#323247]
              [&_.coco-slider-handle::after]:hidden
              [&_.coco-slider-handle::before]:hidden [&_.coco-slider-handle]:top-0.5
              [&_.coco-slider-handle]:size-2 [&_.coco-slider-handle]:rounded-full
              [&_.coco-slider-handle]:border-[1.6px] [&_.coco-slider-handle]:border-white
            `}
          />

          <div className='mx-auto flex items-center gap-3'>
            {actualProducts.map((product, index) => (
              <span
                key={product.priceId}
                className={getTierLabelClassName(
                  index,
                  selectedIndex,
                  actualProducts.length,
                )}
              >
                {product.usageCount}
              </span>
            ))}
          </div>
        </div>
      </div>

      {buyButton}

      {selectedProduct ? (
        <ListItem
          className='mt-5'
          textList={featureItems}
          liClassName={(enabled) =>
            cls`
              items-start text-xs 
              ${enabled ? 'text-f-text-tertiary' : 'text-f-text-disabled'}
            `
          }
          iconClassName='mt-0 size-4'
        />
      ) : null}
    </li>
  )
}

function Inner(
  props: Pick<ICommonPlansBlockProps, 'className' | 'ulClassName'>,
) {
  const { className, ulClassName } = props
  const { payStepInfo, isActive, isAnonymousPayment, isModal } =
    usePlansContext()

  return (
    <InnerContent
      isAnonymousPayment={isAnonymousPayment}
      className={tw`
        mx-auto
        ${payStepInfo.step === EPricingStep.PAY ? 'max-w-[1080px]' : 'max-w-[832px]'}
        ${className}
      `}
      ulClassName={tw`
        grid-cols-1 justify-items-center gap-4 md:grid-cols-3
        ${isModal ? 'mt-8' : ''}
        ${ulClassName}
      `}
      paymentContentProps={{
        payStepInfo,
        isActive,
      }}
      payClassName={cls`
        mx-auto
        max-w-[1060px]
      `}
      SignUpBox={
        <SignUpBox
          onSignInSuccess={() => {}}
          className={cls`bg-f-bg-container w-full max-w-[542px] border-none`}
          signUpWarperClassName='px-4 text-center'
        />
      }
      AfterBuyContent={
        <>
          <div className='mt-4 text-center text-sm text-white/60'>
            <Trans>Note: Each song costs {MUSIC_DEFAULT_CREDITS} credits</Trans>
          </div>
          <PayCard />
        </>
      }
    >
      {experimentPlanConfigs.map((config) => (
        <ExperimentPlanCard key={config.key} config={config} />
      ))}
    </InnerContent>
  )
}

export interface IExperimentVariantAProps extends ICommonPlansBlockProps {}

const ExperimentVariantA: React.FC<IExperimentVariantAProps> = (props) => {
  const { className, productList, ulClassName, ...initialValue } = props

  if (!(productList && productList.length)) {
    return null
  }

  return (
    <PlansProvider
      initialValue={{
        ...initialValue,
        productList,
      }}
    >
      <Inner className={className} ulClassName={ulClassName} />
    </PlansProvider>
  )
}

export default ExperimentVariantA
